import { Component, Input, WritableSignal, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslationService } from '../../../../core/services/translation.service';
import { TranslatePipe } from '../../../../shared/pipes/translate.pipe';

export type SheetFormatEditorUnit = 'in' | 'mm';

export interface SheetFormatEditorModel {
  unit: SheetFormatEditorUnit;
  name: string;
  product_code: string;
  manufacturer: string;
  page_width: number;
  page_height: number;
  label_width: number;
  label_height: number;
  columns: number;
  rows: number;
  margin_top: number;
  margin_left: number;
  gutter_horizontal: number;
  gutter_vertical: number;
  corner_radius: number;
}

const MM_PER_INCH = 25.4;

const DIMENSION_KEYS: Array<
  keyof Pick<
    SheetFormatEditorModel,
    | 'page_width'
    | 'page_height'
    | 'label_width'
    | 'label_height'
    | 'margin_top'
    | 'margin_left'
    | 'gutter_horizontal'
    | 'gutter_vertical'
    | 'corner_radius'
  >
> = [
  'page_width',
  'page_height',
  'label_width',
  'label_height',
  'margin_top',
  'margin_left',
  'gutter_horizontal',
  'gutter_vertical',
  'corner_radius',
];

@Component({
  selector: 'app-sheet-format-editor-modal',
  standalone: true,
  imports: [ReactiveFormsModule, FormlyModule, TranslatePipe],
  templateUrl: './sheet-format-editor-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sheet-format-editor-modal.component.css',
})
export class SheetFormatEditorModalComponent {
  private translate = inject(TranslationService);

  @Input({ required: true }) modelSignal!: WritableSignal<SheetFormatEditorModel>;
  @Input() saveDisabledSignal?: WritableSignal<string | null>;

  form = new FormGroup({});
  model: SheetFormatEditorModel = {
    unit: 'in',
    name: '',
    product_code: '',
    manufacturer: '',
    page_width: 8.5,
    page_height: 11,
    label_width: 2,
    label_height: 1,
    columns: 3,
    rows: 10,
    margin_top: 0,
    margin_left: 0,
    gutter_horizontal: 0,
    gutter_vertical: 0,
    corner_radius: 0,
  };
  fields: FormlyFieldConfig[] = [];

  ngOnInit(): void {
    this.model = { ...this.modelSignal() };
    this.fields = this.buildFields(this.model.unit);
    this.updateSaveDisabled();
  }

  onModelChange(updated: SheetFormatEditorModel): void {
    let next = { ...updated };
    const prevUnit = this.model.unit;
    const nextUnit = updated.unit;

    if (nextUnit !== prevUnit) {
      next = this.convertUnits(next, prevUnit, nextUnit);
      this.fields = this.buildFields(nextUnit);
    }

    this.model = next;
    this.modelSignal.set(this.model);
    this.updateSaveDisabled();
  }

  private updateSaveDisabled(): void {
    if (!this.saveDisabledSignal) {
      return;
    }
    if (this.form.valid) {
      this.saveDisabledSignal.set(null);
      return;
    }
    this.saveDisabledSignal.set(
      this.translate.translate('labelPrinting.sheetFormatEditor.validation.fixErrors'),
    );
  }

  private convertUnits(
    model: SheetFormatEditorModel,
    fromUnit: SheetFormatEditorUnit,
    toUnit: SheetFormatEditorUnit,
  ): SheetFormatEditorModel {
    if (fromUnit === toUnit) {
      return model;
    }

    const factor = fromUnit === 'in' ? MM_PER_INCH : 1 / MM_PER_INCH;
    const converted = { ...model };

    for (const key of DIMENSION_KEYS) {
      converted[key] = Number((converted[key] * factor).toFixed(toUnit === 'in' ? 4 : 2));
    }

    return converted;
  }

  private unitSuffix(unit: SheetFormatEditorUnit): string {
    return unit === 'in' ? 'in' : 'mm';
  }

  private numericStep(unit: SheetFormatEditorUnit): number {
    return unit === 'in' ? 0.001 : 0.1;
  }

  private buildFields(unit: SheetFormatEditorUnit): FormlyFieldConfig[] {
    const suffix = this.unitSuffix(unit);
    const step = this.numericStep(unit);

    return [
      {
        key: 'name',
        type: 'input',
        className: 'flex-basis-full',
        props: {
          label: this.translate.translate('labelPrinting.sheetFormatEditor.field.name'),
          required: true,
          maxLength: 120,
        },
      },
      {
        key: 'product_code',
        type: 'input',
        props: {
          label: this.translate.translate('labelPrinting.sheetFormatEditor.field.productCode'),
          maxLength: 60,
        },
      },
      {
        key: 'manufacturer',
        type: 'input',
        props: {
          label: this.translate.translate('labelPrinting.sheetFormatEditor.field.manufacturer'),
          maxLength: 120,
        },
      },
      {
        key: 'unit',
        type: 'native-select',
        props: {
          label: this.translate.translate('labelPrinting.sheetFormatEditor.field.unit'),
          showLabel: true,
          options: [
            {
              value: 'in',
              label: this.translate.translate('labelPrinting.sheetFormatEditor.unit.inches'),
            },
            {
              value: 'mm',
              label: this.translate.translate('labelPrinting.sheetFormatEditor.unit.mm'),
            },
          ],
        },
      },
      {
        key: 'page_width',
        type: 'number',
        props: {
          label: `${this.translate.translate('labelPrinting.sheetFormatEditor.field.pageWidth')} (${suffix})`,
          required: true,
          min: unit === 'in' ? 1 : 25,
          max: unit === 'in' ? 20 : 500,
          step,
        },
      },
      {
        key: 'page_height',
        type: 'number',
        props: {
          label: `${this.translate.translate('labelPrinting.sheetFormatEditor.field.pageHeight')} (${suffix})`,
          required: true,
          min: unit === 'in' ? 1 : 25,
          max: unit === 'in' ? 30 : 760,
          step,
        },
      },
      {
        key: 'label_width',
        type: 'number',
        props: {
          label: `${this.translate.translate('labelPrinting.sheetFormatEditor.field.labelWidth')} (${suffix})`,
          required: true,
          min: unit === 'in' ? 0.1 : 2,
          max: unit === 'in' ? 10 : 250,
          step,
        },
      },
      {
        key: 'label_height',
        type: 'number',
        props: {
          label: `${this.translate.translate('labelPrinting.sheetFormatEditor.field.labelHeight')} (${suffix})`,
          required: true,
          min: unit === 'in' ? 0.1 : 2,
          max: unit === 'in' ? 10 : 250,
          step,
        },
      },
      {
        key: 'columns',
        type: 'number',
        props: {
          label: this.translate.translate('labelPrinting.sheetFormatEditor.field.columns'),
          required: true,
          min: 1,
          max: 50,
          step: 1,
        },
      },
      {
        key: 'rows',
        type: 'number',
        props: {
          label: this.translate.translate('labelPrinting.sheetFormatEditor.field.rows'),
          required: true,
          min: 1,
          max: 80,
          step: 1,
        },
      },
      {
        key: 'margin_top',
        type: 'number',
        props: {
          label: `${this.translate.translate('labelPrinting.sheetFormatEditor.field.marginTop')} (${suffix})`,
          required: true,
          min: 0,
          max: unit === 'in' ? 5 : 120,
          step,
        },
      },
      {
        key: 'margin_left',
        type: 'number',
        props: {
          label: `${this.translate.translate('labelPrinting.sheetFormatEditor.field.marginLeft')} (${suffix})`,
          required: true,
          min: 0,
          max: unit === 'in' ? 5 : 120,
          step,
        },
      },
      {
        key: 'gutter_horizontal',
        type: 'number',
        props: {
          label: `${this.translate.translate('labelPrinting.sheetFormatEditor.field.gutterHorizontal')} (${suffix})`,
          required: true,
          min: 0,
          max: unit === 'in' ? 2 : 50,
          step,
        },
      },
      {
        key: 'gutter_vertical',
        type: 'number',
        props: {
          label: `${this.translate.translate('labelPrinting.sheetFormatEditor.field.gutterVertical')} (${suffix})`,
          required: true,
          min: 0,
          max: unit === 'in' ? 2 : 50,
          step,
        },
      },
      {
        key: 'corner_radius',
        type: 'number',
        props: {
          label: `${this.translate.translate('labelPrinting.sheetFormatEditor.field.cornerRadius')} (${suffix})`,
          required: true,
          min: 0,
          max: unit === 'in' ? 1 : 25,
          step,
        },
      },
    ];
  }
}
